import React, { useEffect, useMemo, useState } from "react";
import { TreeSelect, Tooltip } from "antd";
import { CloseOutlined, DownOutlined } from "@ant-design/icons";

const AMBER_LIGHT = "#fff4e0";
const AMBER_DARK = "#e08e0b";

// Shared amber "chip" look for both the selected tags and the "+N" overflow.
const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  background: AMBER_LIGHT,
  color: AMBER_DARK,
  borderRadius: 6,
  padding: "1px 7px",
  margin: 2,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: "18px",
};

const nameOf = (item) => item?.name ?? item?.label ?? item?.title ?? "";

// Items carry ocean / land flags (1/0), e.g. { ocean: 1, land: 0 }. A provider
// can be flagged for BOTH — then it belongs to both categories and shows as a
// leaf under each parent (its single `_id` still round-trips to the payload).
const isOcean = (item) => Number(item?.ocean) === 1;
const isLand = (item) => Number(item?.land) === 1;

// Order the groups (parents) are rendered in. "Other" catches providers that
// carry neither flag so nothing silently disappears from the list.
const GROUP_DEFS = [
  { key: "ocean", label: "Ocean", pred: (i) => isOcean(i) },
  { key: "land", label: "Land", pred: (i) => isLand(i) },
  { key: "other", label: "Other", pred: (i) => !isOcean(i) && !isLand(i) },
];

// A leaf's node value is scoped by mode so a both-mode provider can live under
// two parents without a key clash; the real `_id` is recovered on change.
const leafKey = (mode, id) => `${mode}::${id}`;
const idOfLeaf = (k) => {
  const s = String(k);
  const i = s.indexOf("::");
  return i === -1 ? s : s.slice(i + 2);
};

/**
 * Nested multi-select for service providers (antd TreeSelect).
 * - Ocean / Land / Other are collapsible parents (arrow toggles the sub-list);
 *   each parent's checkbox selects/deselects that whole group.
 * - Individual providers are checkable leaves. A provider flagged for both
 *   ocean & land appears under both parents; toggling it anywhere flips the
 *   same underlying `_id` (see handleChange's diff).
 * - The dropdown stays open while checking and only closes on an outside click.
 * - Selected values render as amber chips with a responsive "+N" overflow.
 * - Value is an array of item `_id`s (unchanged contract — the parent form's
 *   Ocean/Land header checkboxes, submit payload and prefill all keep working).
 */
const ProviderSelect = ({
  items = [],
  value = [],
  onChange,
  placeholder = "Select Ocean/Land",
}) => {
  const selectedIds = useMemo(
    () => (Array.isArray(value) ? value.map(String) : []),
    [value]
  );

  const list = useMemo(
    () => (items || []).filter((i) => i && i._id),
    [items]
  );

  const nameById = useMemo(() => {
    const m = {};
    list.forEach((i) => {
      m[String(i._id)] = nameOf(i);
    });
    return m;
  }, [list]);

  // Build the nested tree + the id<->leaf lookups used to translate between the
  // parent's `_id[]` value and TreeSelect's (mode-scoped) leaf keys.
  const { treeData, parentKeys, idToLeafKeys, leafToId } = useMemo(() => {
    const idToLeafKeys = {};
    const leafToId = {};
    const treeData = [];
    const parentKeys = [];
    GROUP_DEFS.forEach(({ key, label, pred }) => {
      const groupItems = list.filter(pred);
      if (!groupItems.length) return;
      const pKey = `grp::${key}`;
      parentKeys.push(pKey);
      treeData.push({
        title: `${label} (${groupItems.length})`,
        value: pKey,
        children: groupItems.map((i) => {
          const id = String(i._id);
          const lk = leafKey(key, id);
          (idToLeafKeys[id] = idToLeafKeys[id] || []).push(lk);
          leafToId[lk] = id;
          return { title: nameOf(i) || id, value: lk };
        }),
      });
    });
    return { treeData, parentKeys, idToLeafKeys, leafToId };
  }, [list]);

  // Controlled value: expand each selected id to all its leaf keys so a
  // both-ocean-and-land provider shows checked under both parents.
  const treeValue = useMemo(
    () => selectedIds.flatMap((id) => idToLeafKeys[id] || []),
    [selectedIds, idToLeafKeys]
  );

  // Expand all groups by default and re-expand when the list arrives (async).
  const [expandedKeys, setExpandedKeys] = useState(parentKeys);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  useEffect(() => {
    setExpandedKeys(parentKeys);
    setAutoExpandParent(true);
  }, [parentKeys]);

  const emit = (ids) => onChange && onChange(Array.from(new Set(ids)));

  // Diff the incoming checked leaves against the current derived value so that
  // toggling ONE leaf of a both-mode provider adds/removes the whole provider
  // (its two leaves are mirrors of a single `_id`, not independent choices).
  const handleChange = (newLeafKeys) => {
    const next = new Set(newLeafKeys);
    const prev = new Set(treeValue);
    const ids = new Set(selectedIds);
    prev.forEach((k) => {
      if (!next.has(k)) ids.delete(leafToId[k] ?? idOfLeaf(k));
    });
    next.forEach((k) => {
      if (!prev.has(k)) ids.add(leafToId[k] ?? idOfLeaf(k));
    });
    emit([...ids]);
  };

  const labelForLeaf = (val, fallback) =>
    nameById[leafToId[val] ?? idOfLeaf(val)] || fallback || val;

  const tagRender = ({ value: val, label, onClose }) => (
    <span
      onMouseDown={(e) => e.stopPropagation()}
      style={{ ...chipStyle, maxWidth: "100%" }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {labelForLeaf(val, label)}
      </span>
      <CloseOutlined
        style={{ fontSize: 9, cursor: "pointer", flexShrink: 0 }}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
    </span>
  );

  return (
    <TreeSelect
      treeData={treeData}
      value={treeValue}
      onChange={handleChange}
      treeCheckable
      showCheckedStrategy={TreeSelect.SHOW_CHILD}
      treeNodeFilterProp="title"
      placeholder={placeholder}
      style={{ width: "100%" }}
      popupClassName="sr-prov-tree-popup"
      listHeight={288}
      allowClear
      maxTagCount="responsive"
      maxTagPlaceholder={(omitted) => {
        const names = omitted.map((o) => labelForLeaf(o.value, o.label));
        return (
          <Tooltip title={names.join(", ")} placement="top">
            <span style={{ ...chipStyle, cursor: "default" }}>
              +{omitted.length}
            </span>
          </Tooltip>
        );
      }}
      tagRender={tagRender}
      treeExpandedKeys={expandedKeys}
      autoExpandParent={autoExpandParent}
      onTreeExpand={(keys) => {
        setExpandedKeys(keys);
        setAutoExpandParent(false);
      }}
      onSearch={(val) => {
        // Expand everything while searching so matches are always visible.
        if (val) {
          setExpandedKeys(parentKeys);
          setAutoExpandParent(true);
        }
      }}
      switcherIcon={(props) => {
        if (!props || props.isLeaf) return null;
        return (
          <DownOutlined
            style={{ fontSize: 11 }}
            rotate={props.expanded ? 0 : -90}
          />
        );
      }}
      getPopupContainer={(trigger) => trigger.parentNode}
    />
  );
};

export default ProviderSelect;
